// src/pages/HomePage.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight,
  Star,
  ArrowRight
} from 'lucide-react';
import Header from './components/Header';
import { productAPI } from './services/api';

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hotProducts, setHotProducts] = useState([]); // 新增：儲存熱門商品
  const [loadingProducts, setLoadingProducts] = useState(false);

  // 新增：載入熱門商品
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const products = await productAPI.getPopularProducts(10);
        setHotProducts(products);
      } catch (error) {
        console.error('載入熱門商品失敗:', error);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // 活動輪播數據 - 4個促銷活動
  const promotionBanners = [
    {
      id: 1,
      title: '雙11購物節',
      subtitle: '全站最低價',
      description: '精選商品5折起，滿千免運',
      bgGradient: 'from-orange-500 via-red-500 to-pink-500',
      ctaText: '立即搶購',
      ctaLink: '/products?sale=true',
      icon: '🔥',
      features: ['5折起', '滿千免運', '限時優惠']
    },
    {
      id: 2,
      title: '新用戶專享',
      subtitle: '首購禮遇',
      description: '註冊即送TWD100優惠券',
      bgGradient: 'from-blue-500 via-purple-500 to-pink-500',
      ctaText: '立即註冊',
      ctaLink: '/auth',
      icon: '🎁',
      features: ['TWD100優惠券', '免運券', '會員積分']
    },
    {
      id: 3,
      title: '品牌特賣會',
      subtitle: '國際大牌',
      description: '精選品牌商品3折起，限時限量',
      bgGradient: 'from-green-500 via-teal-500 to-blue-500',
      ctaText: '查看品牌',
      ctaLink: '/products?featured=true',
      icon: '⭐',
      features: ['3折起', '正品保證', '品牌直營']
    },
    {
      id: 4,
      title: '免運大放送',
      subtitle: '全場免運',
      description: '不限金額，全站商品免運費',
      bgGradient: 'from-purple-500 via-pink-500 to-red-500',
      ctaText: '開始購物',
      ctaLink: '/products',
      icon: '🚚',
      features: ['全場免運', '快速到貨', '安心退換']
    }
  ];

  // 自動輪播
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % promotionBanners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [promotionBanners.length]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 統一的導航欄 - homepage variant */}
      <Header variant="homepage" />

      {/* 主要內容 */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* 活動輪播廣告 - 調整高度和響應式設計 */}
        <div className="relative h-[28rem] md:h-[32rem] rounded-2xl overflow-hidden mb-8 shadow-2xl">
          {promotionBanners.map((banner, index) => (
            <div
              key={banner.id}
              className={`absolute inset-0 bg-gradient-to-br ${banner.bgGradient} transition-all duration-700 ${
                index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
              }`}
            >
              <div className="absolute inset-0 flex items-center justify-between px-8 md:px-16 py-8">
                {/* 左側內容 */}
                <div className="text-white max-w-xl">
                  {/* 圖標 */}
                  <div className="text-6xl md:text-7xl mb-3 md:mb-4 animate-bounce">
                    {banner.icon}
                  </div>

                  {/* 標題區 */}
                  <div className="mb-4 md:mb-5">
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-2 tracking-tight">
                      {banner.title}
                    </h2>
                    <p className="text-2xl md:text-3xl font-semibold text-white/90">
                      {banner.subtitle}
                    </p>
                  </div>

                  {/* 描述 */}
                  <p className="text-lg md:text-xl mb-5 md:mb-6 text-white/80 font-medium">
                    {banner.description}
                  </p>

                  {/* 特點標籤 */}
                  <div className="flex flex-wrap gap-2 md:gap-3 mb-5 md:mb-6">
                    {banner.features.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="bg-white/20 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold border border-white/30"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* CTA 按鈕 */}
                  <button
                    onClick={() => navigate(banner.ctaLink)}
                    className="bg-white text-gray-900 px-8 md:px-10 py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:bg-gray-100 transform hover:scale-105 transition-all shadow-2xl flex items-center gap-2 md:gap-3 group"
                  >
                    {banner.ctaText}
                    <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                  </button>
                </div>

                {/* 右側裝飾 - 在小螢幕隱藏 */}
                <div className="hidden lg:block text-white/10 text-[16rem] xl:text-[20rem] font-black leading-none">
                  {banner.icon}
                </div>
              </div>
            </div>
          ))}

          {/* 輪播指示器 */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-10">
            {promotionBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-3 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'w-12 bg-white' 
                    : 'w-3 bg-white/50 hover:bg-white/70'
                }`}
              />
            ))}
          </div>

          {/* 左右箭頭 */}
          <button
            onClick={() => setCurrentSlide((prev) => (prev - 1 + promotionBanners.length) % promotionBanners.length)}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all z-10"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={() => setCurrentSlide((prev) => (prev + 1) % promotionBanners.length)}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all z-10"
          >
            <ChevronRight size={32} />
          </button>
        </div>

        {/* 熱門商品區 */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <span className="text-4xl">🔥</span>
              {t('homepage.hotProducts')}
            </h2>
            <Link 
              to="/products" 
              className="text-orange-500 hover:text-orange-600 font-semibold flex items-center gap-2 group"
            >
              {t('homepage.viewAll')}
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>

          {/* 商品網格 */}
          {loadingProducts ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
              <p className="mt-4 text-gray-600">載入中...</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {hotProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
                >
                  {/* 商品圖片 */}
                  <div className="aspect-square bg-gray-100 overflow-hidden">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-4xl">📦</span>
                      </div>
                    )}
                  </div>

                  {/* 商品信息 */}
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-gray-800 mb-2 line-clamp-2 group-hover:text-orange-500 transition-colors">
                      {product.name}
                    </h3>

                    {/* 評分 - 修正：使用 parseFloat 轉換字串為數字 */}
                    <div className="flex items-center gap-1 mb-2">
                      <div className="flex items-center">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs text-gray-600 ml-1">
                          {(parseFloat(product.rating) || 0).toFixed(1)}
                        </span>
                      </div>
                      {product.reviews_count > 0 && (
                        <span className="text-xs text-gray-400">
                          ({product.reviews_count})
                        </span>
                      )}
                    </div>

                    {/* 價格 */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-orange-500">
                        TWD {product.price}
                      </span>
                      {product.original_price && parseFloat(product.original_price) > 0 && (
                        <span className="text-xs text-gray-400 line-through">
                          TWD {product.original_price}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default HomePage;